import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  CREATE_DEVICE_RMA_REPLACEMENT_ORDER,
  CREATE_DEVICE_RMA_RESTOCKING_INVOICE,
  SET_DEVICE_RMA_DISMISSED,
  SET_DEVICE_RMA_REPLACEMENT_ORDER,
  SET_DEVICE_RMA_SHIPPING_ADDRESS,
  DEVICE_RMA_REQUESTS,
  RMA_ORDER_LOOKUP,
  RMA_STRIPE_CUSTOMER,
  SET_DEVICE_RMA_RETURN_RECEIVED,
  SET_DEVICE_RMA_VERIFIED,
  SYNC_DEVICE_RMA_REQUESTS,
} from '@staff-app/graphql-client';
import type {
  DeviceRmaRequest,
  DeviceRmaRequestPage,
  RmaDeviceModel,
  RmaOrderLookup,
} from '@staff-app/shared-types';
import { useDebounced } from './useOrderDeviceTasks';

/** Rows per page. RMAs arrive at a handful a week, so one page holds a long working history. */
export const RMA_PAGE_SIZE = 50;

/**
 * The page sizes the operator may choose.
 *
 * THE SAME THREE, AND THE SAME CONTROL, AS INTERNATIONAL REQUESTS AND THE DEVICES LIST (requester,
 * 2026-09-22). Copied rather than re-decided: staff tables that page differently teach nothing
 * except that they were built on different days.
 *
 * The default drops from 200 to 50 with it. 200 was a single page holding the entire history —
 * which made paging theoretical and the count line decorative — and the queue now carries wide
 * columns (RMA #, model, order numbers, replacement, tracking) that read badly that long.
 */
export const RMA_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

/**
 * The RMA ledger.
 *
 * `verified` is TRI-STATE and must be passed through as a boolean-or-undefined, never coerced:
 * `false` is the unverified working queue — the view an operator actually lives in — and folding
 * it into "no filter" would silently show them everything.
 */
export function useDeviceRmaRequests(
  search: string,
  deviceModel: string,
  verified: boolean | undefined,
  offset = 0,
  limit: number = RMA_PAGE_SIZE,
  /**
   * Omitted = the WORKING QUEUE (dismissed rows hidden), `true` = only dismissed. Deliberately not
   * symmetric with `verified`, where omitted means everything: a dismissed request is not work, and
   * showing it by default would undo the point of dismissing it.
   */
  dismissed?: boolean,
) {
  const debouncedSearch = useDebounced(search.trim(), 300);
  return useQuery<{ deviceRmaRequests: DeviceRmaRequestPage }>(DEVICE_RMA_REQUESTS, {
    variables: {
      filter: {
        search: debouncedSearch || undefined,
        deviceModel: (deviceModel || undefined) as RmaDeviceModel | undefined,
        verified,
        dismissed,
      },
      pagination: { offset, limit },
    },
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * Stamp or clear the eligibility verification.
 *
 * Refetches the list rather than updating optimistically — there is no `optimisticResponse`
 * anywhere in this app, and the reason holds here: the server decides, and when it refuses (a row
 * the poll has not ingested yet, a missing migration) the message is worth showing verbatim rather
 * than being papered over by a row that already flipped.
 *
 * The refetch also matters when the filter is `verified: false`: verifying a row removes it from
 * the queue being viewed, which is the correct behaviour and only happens if the list re-reads.
 */
export function useSetDeviceRmaVerified() {
  return useMutation<
    { setDeviceRmaVerified: DeviceRmaRequest },
    {
      submissionId: string;
      verified: boolean;
      /** Partial: omitted leaves the stored value alone, '' clears it. */
      previousOrderNumber?: string | null;
      troubleshootingNotes?: string | null;
      /** The verifier's corrected quantity. Omitted leaves it alone; 0 clears the correction. */
      deviceCount?: number | null;
      /** `YYYY-MM-DD` the device was bought. Omitted leaves it alone; '' clears it. */
      orderDate?: string | null;
    }
  >(SET_DEVICE_RMA_VERIFIED, { refetchQueries: ['deviceRmaRequests'] });
}

/**
 * Take a request out of the queue, or restore it (`reason: null`).
 *
 * Refetches the list, because a dismissal REMOVES the row from the default view — the operator's
 * confirmation that it worked is the row disappearing.
 */
export function useSetDeviceRmaDismissed() {
  return useMutation<
    { setDeviceRmaDismissed: DeviceRmaRequest },
    { submissionId: string; reason?: string | null; duplicateOfRmaNumber?: number | null }
  >(SET_DEVICE_RMA_DISMISSED, { refetchQueries: ['deviceRmaRequests'] });
}

/** A corrected shipping address, exactly as the mutation's input type wants it. */
export interface RmaShipToInput {
  name: string;
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  zip: string;
  country: string;
}

/**
 * Correct — or clear — where the replacement ships.
 *
 * Refetches the list: the drawer and the queue both read the address, and a correction that is
 * visible in one place and stale in another is how the wrong one gets acted on. Passing no address
 * clears the correction.
 */
export function useSetDeviceRmaShippingAddress() {
  return useMutation<
    { setDeviceRmaShippingAddress: DeviceRmaRequest },
    { submissionId: string; address?: RmaShipToInput | null }
  >(SET_DEVICE_RMA_SHIPPING_ADDRESS, { refetchQueries: ['deviceRmaRequests'] });
}

/**
 * Record a replacement raised by hand, and/or its tracking number.
 *
 * Refetches the list, because both values are now COLUMNS in the queue — a tracking number visible
 * in the dialog and absent from the table is how somebody concludes nothing has shipped.
 */
export function useSetDeviceRmaReplacementOrder() {
  return useMutation<
    { setDeviceRmaReplacementOrder: DeviceRmaRequest },
    { submissionId: string; orderNumber?: string | null; trackingNumber?: string | null; trackingUrl?: string | null }
  >(SET_DEVICE_RMA_REPLACEMENT_ORDER, { refetchQueries: ['deviceRmaRequests'] });
}

/**
 * Release the replacement — create the Shopify order for a verified RMA.
 *
 * Refetches the list like its siblings, because the row gains an order number the queue and the
 * drawer both show. NO `optimisticResponse`, and here the rule matters more than anywhere else on
 * this surface: this mutation causes a physical shipment, and a row that flipped to "ordered"
 * before Shopify answered would be a claim that a device is on its way when it may not be.
 *
 * Errors are shown VERBATIM by the dialog. They are the useful part — a missing Shopify scope, an
 * address Shopify will not accept, an unresolvable SKU — and each names something a human can go
 * and fix.
 */
export function useCreateDeviceRmaReplacementOrder() {
  return useMutation<
    { createDeviceRmaReplacementOrder: DeviceRmaRequest },
    { submissionId: string }
  >(CREATE_DEVICE_RMA_REPLACEMENT_ORDER, { refetchQueries: ['deviceRmaRequests'] });
}

/** Poll HubSpot now instead of waiting for the timer. Idempotent, so pressing it twice is safe. */
export function useSyncDeviceRmaRequests() {
  return useMutation<{ syncDeviceRmaRequests: number }, Record<string, never>>(
    SYNC_DEVICE_RMA_REQUESTS,
    { refetchQueries: ['deviceRmaRequests'] },
  );
}

/**
 * Record — or clear — receipt of the returned unit.
 *
 * Refetches the list like its sibling: the return-status column is what an operator is scanning
 * for, so it has to change under them when they record one.
 */
export function useSetDeviceRmaReturnReceived() {
  return useMutation<
    { setDeviceRmaReturnReceived: DeviceRmaRequest },
    {
      submissionId: string;
      condition: string | null;
      missingItems?: string | null;
      /** `WORKS` | `FAULTY` | `NOT_TESTED`. Omitted leaves it alone; '' clears it. */
      testedOutcome?: string | null;
      testNotes?: string | null;
    }
  >(SET_DEVICE_RMA_RETURN_RECEIVED, { refetchQueries: ['deviceRmaRequests'] });
}

/**
 * Resolve an order number to its purchase date — LAZY, not on render.
 *
 * A live Shopify read, so firing one per keystroke while an operator types an order number would
 * be wasteful and noisy in Shopify's logs. Same rule, and the same reason, as
 * `useRmaStripeCustomer`. `found: false` is the ordinary answer: about half of real RMAs are Amazon
 * purchases whose numbers cannot resolve in Shopify at all.
 */
export function useRmaOrderLookup() {
  return useLazyQuery<{ rmaOrderLookup: RmaOrderLookup }, { orderNumber: string }>(
    RMA_ORDER_LOOKUP,
    { fetchPolicy: 'network-only' },
  );
}

/** A Stripe customer as the confirm step needs it. */
export interface RmaStripeCustomer {
  id: string;
  name?: string | null;
  email?: string | null;
  deleted: boolean;
}

/**
 * Resolve `cus_…` on demand — LAZY, not on render: it is a live Stripe read, and firing one per
 * keystroke while an operator types an id would be both wasteful and noisy in Stripe's logs.
 */
export function useRmaStripeCustomer() {
  return useLazyQuery<{ rmaStripeCustomer: RmaStripeCustomer }, { stripeCustomerId: string }>(
    RMA_STRIPE_CUSTOMER,
    { fetchPolicy: 'network-only' },
  );
}

/** Raise the restocking fee. Refetches the list so the charged state shows immediately. */
export function useCreateRmaRestockingInvoice() {
  return useMutation<
    { createDeviceRmaRestockingInvoice: DeviceRmaRequest },
    { submissionId: string; stripeCustomerId: string; comment: string }
  >(CREATE_DEVICE_RMA_RESTOCKING_INVOICE, { refetchQueries: ['deviceRmaRequests'] });
}
